import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { products } from '../../data/products';
import { useCart } from '../../context/CartContext';
import RelatedProducts from '../../components/RelatedProducts';

const HDMICable = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === 24) || products[23]; // HDMI Cable
  const { addToCart } = useCart();

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedLength, setSelectedLength] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const images = [
    "/images/shop-products/hdmi-cable/1.jpeg",
    "/images/shop-products/hdmi-cable/hdtv_cable_8k_48gpbs_8k60hz_hd_1689439785_3d040cd3_progressive.jpeg",
    "/images/shop-products/hdmi-cable/hdtv_cable_8k_48gpbs_8k60hz_hd_1689439785_71dd0c4d_progressive.jpeg",
    "/images/shop-products/hdmi-cable/hdtv_cable_8k_48gpbs_8k60hz_hd_1689439785_72592b32_progressive.jpeg",
    "/images/shop-products/hdmi-cable/hdtv_cable_8k_48gpbs_8k60hz_hd_1689439785_7c946700_progressive.jpeg",
    "/images/shop-products/hdmi-cable/O1CN01WWkzp32K85jwDzfrk_!!2073209511-0-cib.jpg",
    "/images/shop-products/hdmi-cable/sg-11134201-22120-0p5auo18allvbd.jpeg",
    "/images/shop-products/hdmi-cable/sg-11134201-22120-et5b5o18allv26.jpeg",
    "/images/shop-products/hdmi-cable/sg-11134201-22120-hcjbko18allvec.jpeg"
  ];

  // Simple image switching
  const handleImageClick = (index) => {
    setCurrentImage(index);
  };

  // Handle Add to Cart
  const handleAddToCart = () => {
    if (!selectedLength) return;
    
    const selectedLengthData = lengths.find(l => l.name === selectedLength);
    const cartItem = {
      id: product.id,
      name: `${product.name} (${selectedLength})`,
      price: selectedLengthData.price,
      image: product.image,
      quantity: quantity,
      length: selectedLength
    };
    
    addToCart(cartItem);
    
    // Successfully added to cart (no popup)
  };

  // Handle Buy Now
  const handleBuyNow = () => {
    if (!selectedLength) return;
    
    // Add to cart first
    handleAddToCart();
    
    // Navigate to shopping cart
    navigate('/cart');
  };

  const lengths = [
    { name: '1.5m', price: 9.90 },
    { name: '2m', price: 11.90 },
    { name: '3m', price: 13.90 }
  ];

  const breadcrumbs = [
    { name: 'Home', href: '/' },
    { name: 'Cables & Connectors', href: '/category/cables' },
    { name: product.name, href: '#' }
  ];

  const relatedProducts = products
    .filter(p => p.category === 'cables' && p.id !== product.id)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Breadcrumbs */}
      <nav className="bg-gray-800 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-2 text-sm text-gray-300">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.name}>
                {index > 0 && <span>/</span>}
                <Link
                  to={crumb.href}
                  className="hover:text-white transition-colors"
                >
                  {crumb.name}
                </Link>
              </React.Fragment>
            ))}
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 text-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-gray-800 rounded-lg overflow-hidden">
              <img
                src={images[currentImage]}
                alt={product.name}
                className="w-full h-full object-cover"
                onLoad={() => {
                  console.log(`✔ Main image loaded successfully:`, images[currentImage]);
                }}
                onError={(e) => {
                  console.error(`❌ Main image failed to load:`, images[currentImage]);
                  e.target.src = '/images/products/placeholder.jpg';
                }}
              />
            </div>
            <div className="grid grid-cols-5 gap-1">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => handleImageClick(index)}
                  className={`aspect-square bg-gray-800 rounded-lg overflow-hidden border-2 transition-colors ${
                    currentImage === index ? 'border-green-500' : 'border-transparent'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                    onLoad={() => {
                      console.log(`✔ Image ${index + 1} loaded successfully:`, image);
                    }}
                    onError={(e) => {
                      console.error(`❌ Image ${index + 1} failed to load:`, image);
                      e.target.src = '/images/products/placeholder.jpg';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">{product.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-600'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="ml-2 text-gray-300">{product.rating}</span>
                </div>
                <span className="text-gray-400">({product.reviews} reviews)</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-white">
                  S${selectedLength ? lengths.find(l => l.name === selectedLength)?.price.toFixed(2) : '9.90'}
                </span>
                <span className="text-lg text-gray-400">Starting from</span>
              </div>

              <div className="flex items-center space-x-2">
                <span className={`w-3 h-3 rounded-full ${product.inStock ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className={product.inStock ? 'text-green-400' : 'text-red-400'}>
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>

              <p className="text-gray-300 text-lg leading-relaxed">{product.description}</p>
            </div>

            {/* Product Options */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-3">Choose Length</h3>
                <div className="flex gap-3">
                  {lengths.map((length) => (
                    <button
                      key={length.name}
                      onClick={() => setSelectedLength(length.name)}
                      className={`px-6 py-3 rounded-lg border-2 transition-colors ${
                        selectedLength === length.name
                          ? 'border-green-500 bg-green-500 text-white'
                          : 'border-gray-600 text-gray-300 hover:border-green-400'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-medium">{length.name}</div>
                        <div className="text-sm opacity-80">S${length.price}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Quantity</label>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg border border-gray-600 text-gray-300 hover:border-gray-400 transition-colors"
                  >
                    -
                  </button>
                  <span className="w-16 text-center text-lg font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-lg border border-gray-600 text-gray-300 hover:border-gray-400 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <button 
                onClick={handleAddToCart}
                className={`w-full font-bold py-4 px-6 rounded-lg transition-colors ${
                  selectedLength 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                }`}
                disabled={!selectedLength}
              >
                {selectedLength 
                  ? `Add to Cart - S$${((lengths.find(l => l.name === selectedLength)?.price || 9.90) * quantity).toFixed(2)}`
                  : 'Please Select Length'
                }
              </button>
              <button 
                onClick={handleBuyNow}
                className={`w-full font-bold py-4 px-6 rounded-lg transition-colors ${
                  selectedLength 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                }`}
                disabled={!selectedLength}
              >
                {selectedLength ? 'Buy Now' : 'Please Select Length'}
              </button>
            </div>

             {/* Shipping Info */}
             <div className="bg-gray-800 p-4 rounded-lg">
               <div className="space-y-3">
                 <div className="flex items-center space-x-3">
                   <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                     <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                     <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.5-2A1 1 0 0015 7h-3V4a1 1 0 00-1-1H3zM14 7l-1.5 2H10V5h4v2z" />
                   </svg>
                   <span className="text-gray-300">Free Meet Up (Available at Sengkang & Orchard locations)</span>
                 </div>
                 <div className="flex items-center space-x-3">
                   <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                   </svg>
                   <span className="text-gray-300">Available Shipping</span>
                 </div>
               </div>
             </div>
          </div>
        </div>

        {/* Product Tabs */}
        <div className="mt-16">
          <div className="border-b border-gray-700">
            <nav className="flex space-x-8">
              {['overview', 'specifications', 'reviews'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          <div className="py-8">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Product Overview */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Product Overview</h3>
                  <div className="space-y-4 text-white">
                    <p>
                      Experience crystal-clear 4K video and audio transmission with our premium HDMI cable. 
                      Designed for the most demanding home theater and gaming setups, this cable delivers 
                      uncompressed digital signals with zero signal loss.
                    </p>
                    <p>
                      Perfect for connecting your gaming console, Blu-ray player, streaming device, or 
                      computer to your 4K TV or monitor. The gold-plated connectors ensure maximum 
                      conductivity and corrosion resistance for long-lasting performance.
                    </p>
                    <p>
                      This HDMI cable combines cutting-edge technology with robust construction, making it 
                      both reliable and future-proof for all your high-definition entertainment needs.
                    </p>
                  </div>
                </div>

                {/* Key Features */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Key Features</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">4K Ultra HD Support</div>
                        <div className="text-gray-300 text-sm">Delivers stunning 4K resolution at 60Hz with HDR support for incredible picture quality</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">Gold-Plated Connectors</div>
                        <div className="text-gray-300 text-sm">Premium gold-plated connectors ensure maximum signal integrity and corrosion resistance</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">High-Speed Data Transfer</div>
                        <div className="text-gray-300 text-sm">Supports high-bandwidth data transfer for smooth gaming and video streaming</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">Durable Construction</div>
                        <div className="text-gray-300 text-sm">Heavy-duty construction with reinforced connectors for long-lasting reliability</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">Universal Compatibility</div>
                        <div className="text-gray-300 text-sm">Works with all HDMI-compatible devices including gaming consoles, TVs, and computers</div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'specifications' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Product Specifications */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Product Specifications</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Length</span>
                      <span className="text-white font-medium">2m</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Version</span>
                      <span className="text-white font-medium">HDMI 2.0</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Resolution</span>
                      <span className="text-white font-medium">4K@60Hz</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">HDR Support</span>
                      <span className="text-white font-medium">Yes</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Connector Type</span>
                      <span className="text-white font-medium">Gold Plated</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Cable Type</span>
                      <span className="text-white font-medium">High Speed</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Bandwidth</span>
                      <span className="text-white font-medium">18 Gbps</span>
                    </div>
                  </div>
                </div>

                {/* Features & Benefits */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Features & Benefits</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">4K Ultra HD</h4>
                        <span className="text-blue-400 text-sm">Crystal Clear</span>
                      </div>
                      <p className="text-gray-300 text-sm">Experience stunning 4K resolution with HDR support for the ultimate viewing experience</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Gold Plated</h4>
                        <span className="text-yellow-400 text-sm">Premium</span>
                      </div>
                      <p className="text-gray-300 text-sm">Gold-plated connectors ensure maximum signal integrity and corrosion resistance</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">High Speed</h4>
                        <span className="text-green-400 text-sm">Fast</span>
                      </div>
                      <p className="text-gray-300 text-sm">High-bandwidth data transfer for smooth gaming and video streaming without lag</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Durable Design</h4>
                        <span className="text-orange-400 text-sm">Reliable</span>
                      </div>
                      <p className="text-gray-300 text-sm">Heavy-duty construction with reinforced connectors for long-lasting performance</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Universal Compatibility</h4>
                        <span className="text-purple-400 text-sm">Versatile</span>
                      </div>
                      <p className="text-gray-300 text-sm">Works with all HDMI-compatible devices including gaming consoles, TVs, and computers</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Easy Setup</h4>
                        <span className="text-pink-400 text-sm">Simple</span>
                      </div>
                      <p className="text-gray-300 text-sm">Plug-and-play design with no drivers or software installation required</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white">Customer Reviews</h3>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                    Write a Review
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gray-800 p-6 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">MJ</span>
                        </div>
                        <div>
                          <p className="font-medium text-white">Mike Johnson</p>
                          <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">3 days ago</span>
                    </div>
                    <p className="text-gray-300">
                      这个HDMI线质量很好！4K画质非常清晰，连接稳定，
                      金质接头看起来很高级，性价比很高，强烈推荐！
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-white mb-8">Related Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedProducts.map((relatedProduct) => (
              <Link
                key={relatedProduct.id}
                to={`/product/${relatedProduct.id}`}
                className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors group"
              >
                <div className="aspect-square bg-gray-700 overflow-hidden">
                  <img
                    src={relatedProduct.image}
                    alt={relatedProduct.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-white group-hover:text-blue-400 transition-colors">
                    {relatedProduct.name}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">${relatedProduct.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      
      {/* 相关产品 */}
      <RelatedProducts 
        currentProductId={24} 
        currentCategory="cables" 
        limit={4} 
      />
    </div>
  );
};

export default HDMICable;
